import React from "react";
import { toast } from "sonner";
import { Copy, ShieldAlert, Info, Loader2 } from "lucide-react";
import { riskClass, riskLabel } from "@/lib/format";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const DemoBadge = ({ className = "" }) => (
  <div
    data-testid="demo-badge"
    className={`inline-flex items-center gap-1.5 rounded-md border border-amber-600/40 bg-amber-950/40 px-2 py-1 text-[10px] font-mono font-semibold uppercase tracking-wider text-amber-400 ${className}`}
    title="All market data on PnutScan is clearly-labeled DEMO data for research-sandbox purposes. It is NOT live blockchain data."
  >
    <span className="live-dot" style={{ background: "#fbbf24" }} />
    DEMO DATA
  </div>
);

export const Copyable = ({ value, display, testid }) => (
  <button
    data-testid={testid}
    onClick={(e) => {
      e.stopPropagation();
      navigator.clipboard.writeText(value);
      toast.success("Copied to clipboard", { description: value });
    }}
    className="inline-flex items-center gap-1 font-mono text-xs text-slate-400 hover:text-amber-400 transition-colors"
  >
    {display || value}
    <Copy className="h-3 w-3" />
  </button>
);

export const ScoreChip = ({ type, value, confidence, onClick, testid }) => {
  const cls =
    type === "momentum"
      ? "chip-momentum"
      : type === "organic"
      ? "chip-organic"
      : type === "risk"
      ? riskClass(value)
      : "chip-conf";
  const label =
    type === "momentum" ? "MOM" : type === "organic" ? "ORG" : type === "risk" ? "RISK" : "CONF";
  const content = (
    <>
      <span className="opacity-60">{label}</span>
      {type === "confidence" ? `${value}%` : value}
    </>
  );
  const classes = `inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 font-mono text-xs font-semibold ${cls}`;
  const title = confidence !== undefined ? `Data confidence: ${confidence}%` : label;
  if (!onClick) {
    return (
      <span data-testid={testid} className={classes} title={title}>
        {content}
      </span>
    );
  }
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`${classes} hover:brightness-125 cursor-pointer`}
      title={title}
    >
      {content}
    </button>
  );
};

export const ScoreBar = ({ label, value, tone = "amber" }) => {
  const color =
    tone === "amber" ? "#e6a15c" : tone === "emerald" ? "#10b981" : tone === "rose" ? "#ef4444" : "#06b6d4";
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-slate-400">{label}</span>
        <span className="font-mono font-semibold text-slate-200">{value}</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-800">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${value}%`, background: color }}
        />
      </div>
    </div>
  );
};

const BREAKDOWN_LABELS = {
  volume_acceleration: "Volume acceleration",
  buyer_acceleration: "Buyer acceleration",
  holder_growth: "Holder growth",
  transaction_acceleration: "Transaction acceleration",
  buy_sell_balance: "Buy/sell balance",
  liquidity_growth: "Liquidity growth",
  price_momentum: "Price momentum",
  tracked_wallets: "Tracked wallets",
  narrative: "Narrative confirmation",
  contract: "Contract risk",
  liquidity: "Liquidity risk",
  holder: "Holder risk",
  developer: "Developer risk",
  trading: "Trading risk",
  cluster: "Wallet/cluster risk",
};

export const ScoreBreakdownPopover = ({ type, score, breakdown, confidence, children }) => {
  const tone = type === "momentum" ? "amber" : type === "risk" ? "rose" : "emerald";
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        data-testid={`breakdown-${type}`}
        className="w-80 bg-panel border-slate-700"
        align="start"
      >
        <div className="mb-3 flex items-center justify-between">
          <div className="font-display font-semibold capitalize text-slate-100">
            {type} Score: <span className="font-mono">{score}</span>
          </div>
          {confidence !== undefined && (
            <span className="chip-conf rounded-md px-2 py-0.5 font-mono text-xs">
              Confidence {confidence}%
            </span>
          )}
        </div>
        <div className="space-y-2.5">
          {Object.entries(breakdown || {}).map(([k, v]) => (
            <ScoreBar key={k} label={BREAKDOWN_LABELS[k] || k} value={v} tone={tone} />
          ))}
        </div>
        <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
          Every component is shown transparently — no score is a black box. Data is DEMO.
        </p>
      </PopoverContent>
    </Popover>
  );
};

export const StatCard = ({ label, value, sub, tone, icon: Icon, testid }) => (
  <div data-testid={testid} className="pnut-card p-4 fade-in">
    <div className="flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
      {Icon && <Icon className="h-4 w-4 text-slate-600" />}
    </div>
    <div className="mt-2 font-display text-2xl font-bold text-slate-50">{value}</div>
    {sub !== undefined && (
      <div
        className={`mt-0.5 text-xs font-medium ${
          tone === "pos" ? "num-pos" : tone === "neg" ? "num-neg" : "text-slate-500"
        }`}
      >
        {sub}
      </div>
    )}
  </div>
);

export const PageHeader = ({ title, subtitle, icon: Icon, actions }) => (
  <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <div className="flex items-center gap-2.5">
        {Icon && <Icon className="h-6 w-6 text-amber-400" />}
        <h1 className="font-display text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl">
          {title}
        </h1>
      </div>
      {subtitle && <p className="mt-1.5 max-w-2xl text-sm text-slate-400">{subtitle}</p>}
    </div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

export const SectionTitle = ({ children, right }) => (
  <div className="mb-3 flex items-center justify-between">
    <h3 className="font-display text-sm font-semibold uppercase tracking-wide text-slate-300">
      {children}
    </h3>
    {right}
  </div>
);

export const Loading = ({ label = "Loading intelligence…" }) => (
  <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
    <Loader2 className="h-4 w-4 animate-spin" /> {label}
  </div>
);

export const EmptyState = ({ icon: Icon = Info, title, hint }) => (
  <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-800 py-14 text-center">
    <Icon className="h-6 w-6 text-slate-600" />
    <div className="font-medium text-slate-300">{title}</div>
    {hint && <div className="max-w-md text-xs text-slate-500">{hint}</div>}
  </div>
);

export const EvidenceBox = ({ variant, title, items }) => {
  const good = variant === "interesting";
  return (
    <div
      className={`rounded-lg border p-4 ${
        good ? "border-emerald-900/60 bg-emerald-950/20" : "border-rose-900/60 bg-rose-950/20"
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        {good ? (
          <Info className="h-4 w-4 text-emerald-400" />
        ) : (
          <ShieldAlert className="h-4 w-4 text-rose-400" />
        )}
        <span
          className={`font-display text-xs font-bold uppercase tracking-wider ${
            good ? "text-emerald-400" : "text-rose-400"
          }`}
        >
          {title}
        </span>
      </div>
      <ul className="space-y-1.5">
        {items?.map((it, i) => (
          <li key={i} className="flex gap-2 text-xs text-slate-300">
            <span className={good ? "text-emerald-500" : "text-rose-500"}>›</span>
            {it}
          </li>
        ))}
      </ul>
    </div>
  );
};

export { riskLabel };
